<!--
  DecorationDigitizationCard.vue -- the decoration detail page's
  Digitization card.

  Jake, 2026-10-27: "digitization and approval are two separate things",
  separate but related in the app IA. This card is the digitization half;
  DecorationApprovalCard.vue is the approval half. The link between them:
  an embroidery size opens for approval once a digitized version exists.
  Whether a size can be SENT depends only on its digitization, never on
  its approval.

  What it shows, per digitizable size
  ===================================
  One row per size whose API card carries a `digitization` (the server's
  derived state, `art_prep.decoration_digitization`; null for a method that
  is not digitized). The state reads from that field and nothing else:
  Not sent / Digitizing / Digitized v<N> / Failed. A digitized size names
  the delivered file and its stitch count (`digitized_version`, the
  version the state names; an uploaded stitch file uses its own count).

  Actions
  =======
  * Send / Resend (card level, distributor.digitization.send): THE send,
    POST /api/artworks/<id>/send_to_digitizer, which enqueues a job for
    every current-version size that reads not sent or failed. The unit the
    endpoint sends is the artwork, so the button lives on the card, not on
    a row. 202 -> a toast with the job count and a silent re-read (no page
    spinner); 409 -> the reason code's message inline and in a toast (the
    generic error toast is suppressed so the user reads one clear message).
  * Rework (per digitized size): the digitizer rework loop
    (GET/POST /api/approval_records/<id>/rework, the Approvals workspace's
    ReworkPanel). It is keyed on one of the size's approval records, so a
    size never sent to a client for approval says rework is not open yet.
    An uploaded stitch file (no job) has nothing to rework.

  A size on an EARLIER version of the artwork
  ===========================================
  A send covers only the current version, so such a size is never sent from
  here. It shows a notice and ONE action, "Update to current" (Jake,
  2026-10-28, decided: the only action offered; no "digitize the earlier
  version"). POST /api/artworks/<id>/update_to_current moves EVERY live size
  of the artwork that is on an earlier version onto the current version, so
  the action is the same wherever it is pressed and its dialog counts them
  all. It is free until a send: nothing is sent to the digitizer and nothing
  is charged; approvals of the earlier art stay on record as superseded
  history; a Send afterwards is the real billable commit. The caller needs
  distributor.decoration_library.write (the endpoint enforces it; this gate
  only hides the button).

  The action asks first (a confirm dialog saying exactly that), then calls
  with a busy state, toasts the sizes moved and asks the page to re-read
  (no page spinner). A refusal reads in plain words, inline and in a toast,
  through the same reason-code copy as the send (REFUSAL_MESSAGES): 409
  nothing_to_update / live_job_in_flight (names each job) /
  colorway_bound_to_older_version / live_store_unapproved (names the
  stores), 422 size_aspect_mismatch, 400 no_current_version.

  A live store with no current approval (Jake chose move-and-warn,
  2026-10-28): the default is the 200 whose `unapproved_live_stores` is
  non-empty. It warns, by name, that those stores will use the new art and
  their clients need to re-approve. `refuse` (the 409 live_store_unapproved
  above, which holds the update and names the stores) stays selectable via
  LIVE_STORE_UNAPPROVED_POLICY. BOTH shapes are handled here, so the policy
  is a backend switch only. A host may still replace the action through the scoped
  slot `older-version-actions` ({ card }).

  With no decorations at all, the empty state hosts DigitizeAtSizeDialog
  (#4903): an embroidery artwork never placed is sent at a size the user
  gives, and its send is announced the same way.
-->
<template>
    <section
        class="ddc"
        data-testid="dla-digitization-card"
    >
        <div class="ddc__head">
            <h2 class="ddc__title">
                Digitization
            </h2>
            <span class="ddc__sub">
                The machine embroidery file for each size
            </span>
            <div class="ddc__spacer" />
            <button
                v-if="canSend && sendable.length"
                type="button"
                class="ddc__btn ddc__btn--primary"
                data-testid="dla-digitization-send"
                :disabled="sending"
                @click="send"
            >
                {{ sendLabel }}
            </button>
        </div>

        <p
            v-if="sendError"
            class="ddc__error"
            role="alert"
            data-testid="dla-digitization-send-error"
        >
            {{ sendError }}
        </p>

        <div
            v-if="updateError"
            class="ddc__error"
            role="alert"
            data-testid="dla-update-error"
        >
            <p class="ddc__error-line">
                {{ updateError.message }}
            </p>
            <ul
                v-if="updateError.stores.length"
                class="ddc__list"
            >
                <li
                    v-for="store in updateError.stores"
                    :key="store.id"
                    :data-testid="`dla-update-error-store-${store.id}`"
                >{{ store.name }}</li>
            </ul>
            <ul
                v-if="updateError.jobs.length"
                class="ddc__list"
            >
                <li
                    v-for="job in updateError.jobs"
                    :key="job.id"
                    :data-testid="`dla-update-error-job-${job.id}`"
                >{{ jobLine(job) }}</li>
            </ul>
        </div>

        <div
            v-if="updateWarning.length"
            class="ddc__warning"
            role="status"
            data-testid="dla-update-warning"
        >
            <p class="ddc__error-line">
                The sizes moved. These live stores will use the new art, and
                their clients need to re-approve it:
            </p>
            <ul class="ddc__list">
                <li
                    v-for="store in updateWarning"
                    :key="store.id"
                    :data-testid="`dla-update-warning-store-${store.id}`"
                >{{ store.name }}</li>
            </ul>
            <button
                type="button"
                class="ddc__btn ddc__btn--sm"
                data-testid="dla-update-warning-dismiss"
                @click="updateWarning = []"
            >
                Dismiss
            </button>
        </div>

        <div
            v-if="!cards.length"
            class="ddc__empty"
            data-testid="dla-digitization-empty"
        >
            <p class="ddc__muted">
                Nothing to digitize yet: this artwork has no sizes.
            </p>
            <DigitizeAtSizeDialog
                :artwork-id="artworkId"
                :capabilities="capabilities"
                @sent="onSent"
            />
        </div>

        <p
            v-else-if="!rows.length"
            class="ddc__muted"
            data-testid="dla-digitization-none"
        >
            None of these sizes is digitized: their methods need no
            digitizing.
        </p>

        <div
            v-for="card in rows"
            :key="card.id"
            class="ddc__row"
            :data-testid="`dla-digitization-row-${card.id}`"
        >
            <div class="ddc__row-main">
                <span class="ddc__method">{{ card.methodLabel }}</span>
                <span class="ddc__dims">{{ dimsLabel(card) }}</span>
                <span
                    class="ddc__state"
                    :class="`ddc__state--${stateOf(card)}`"
                    :data-state="stateOf(card)"
                    :data-testid="`dla-digitization-state-${card.id}`"
                >{{ stateLabel(card) }}</span>
                <div class="ddc__spacer" />
                <button
                    v-if="canRework(card)"
                    type="button"
                    class="ddc__btn ddc__btn--sm"
                    :data-testid="`dla-digitization-rework-${card.id}`"
                    :disabled="reworkLoading"
                    @click="openRework(card)"
                >
                    Rework
                </button>
            </div>

            <div
                v-if="stateOf(card) === 'digitized'"
                class="ddc__file"
            >
                <span
                    v-if="fileName(card)"
                    class="ddc__file-name"
                    :data-testid="`dla-digitization-file-${card.id}`"
                    :title="storedNameHint(card)"
                >{{ fileName(card) }}</span>
                <span
                    v-if="stitchLabel(card)"
                    class="ddc__muted"
                    :data-testid="`dla-digitization-stitches-${card.id}`"
                >{{ stitchLabel(card) }}</span>
                <span
                    v-if="!hasJob(card)"
                    class="ddc__muted"
                    :data-testid="`dla-digitization-uploaded-${card.id}`"
                >Uploaded stitch file</span>
                <span
                    v-if="reworkUnavailable(card)"
                    class="ddc__muted"
                    :data-testid="`dla-digitization-rework-unavailable-${card.id}`"
                >Rework opens once this size has been sent to a client for
                    approval.</span>
            </div>

            <div
                v-if="card.on_current_version === false"
                class="ddc__notice"
                :data-testid="`dla-digitization-older-version-${card.id}`"
            >
                <span>This size is on an earlier version of the artwork</span>
                <slot
                    name="older-version-actions"
                    :card="card"
                >
                    <button
                        v-if="canUpdate"
                        type="button"
                        class="ddc__btn ddc__btn--sm"
                        :data-testid="`dla-digitization-update-${card.id}`"
                        :disabled="updating"
                        @click="openUpdate"
                    >
                        Update to current
                    </button>
                </slot>
            </div>

            <div
                v-if="reworkFor === card.id"
                class="ddc__rework"
            >
                <ReworkPanel
                    v-if="reworkPrefill.loaded"
                    :prefill="reworkPrefill.instruction"
                    :can-submit="reworkPrefill.canSubmit"
                    :pending="reworkPending"
                    :error="reworkError"
                    @submit="submitRework"
                />
                <p
                    v-if="reworkPrefill.loaded && !reworkPrefill.canSubmit"
                    class="ddc__muted"
                    :data-testid="`dla-digitization-rework-closed-${card.id}`"
                >
                    There is no digitizer job to rework for this size.
                </p>
                <p
                    v-if="!reworkPrefill.loaded && reworkError"
                    class="ddc__error"
                    role="alert"
                >
                    {{ reworkError }}
                </p>
                <button
                    type="button"
                    class="ddc__btn ddc__btn--sm"
                    :data-testid="`dla-digitization-rework-cancel-${card.id}`"
                    :disabled="reworkPending"
                    @click="closeRework"
                >
                    Close
                </button>
            </div>
        </div>

        <div
            v-if="updateOpen"
            class="ddc-backdrop"
            @click.self="cancelUpdate"
        >
            <div
                class="ddc-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ddc-update-title"
                tabindex="-1"
                data-testid="dla-update-dialog"
                @keydown.esc="cancelUpdate"
            >
                <h3
                    id="ddc-update-title"
                    class="ddc-dialog__title"
                >
                    Update to the current artwork
                </h3>
                <p class="ddc-dialog__line">
                    {{ olderCount }} {{ olderCount === 1 ? 'size' : 'sizes' }} on an earlier version of this artwork will move to the current artwork.
                </p>
                <p class="ddc-dialog__line">
                    Approvals of the earlier art stay on record but no longer count.
                </p>
                <p class="ddc-dialog__line">
                    Nothing is sent to the digitizer and nothing is charged until the owner presses Send.
                </p>
                <div class="ddc-dialog__footer">
                    <button
                        type="button"
                        class="ddc__btn"
                        ref="updateCancel"
                        data-testid="dla-update-cancel"
                        :disabled="updating"
                        @click="cancelUpdate"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="ddc__btn ddc__btn--primary"
                        data-testid="dla-update-confirm"
                        :disabled="updating"
                        @click="confirmUpdate"
                    >
                        {{ updating ? 'Updating...' : 'Update to current' }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import axios from '@/axios'
import { SUPPRESS_ERROR_TOAST } from '@/interceptors/authResponseInterceptor'
import ReworkPanel from '@/pages/distributor/approvals/ReworkPanel.vue'
import DigitizeAtSizeDialog from './DigitizeAtSizeDialog.vue'

const SEND_CAP = 'distributor.digitization.send'
// Update to current is a library edit that bills nothing (not the send cap).
const UPDATE_CAP = 'distributor.decoration_library.write'

// The derived states a send creates a job for (art_prep.
// SENDABLE_DIGITIZATION_STATES).
const SENDABLE = new Set(['not_sent', 'failed'])

const STATE_LABELS = {
    not_sent: 'Not sent',
    digitizing: 'Digitizing',
    failed: 'Failed',
}

// What a refused send (409 ``reason``) means, in the user's words. An
// unknown code falls back to the server's own message.
const REFUSAL_MESSAGES = {
    already_covered_by_live_job:
        'Nothing new was sent: every size that needs digitizing is already being digitized.',
    already_digitized:
        'Nothing was sent: every size is already digitized. To change one, use Rework.',
    no_live_decoration_on_current_version:
        "Nothing was sent: no size is on the artwork's current version.",
    // Update to current (POST /api/artworks/<id>/update_to_current).
    nothing_to_update:
        'Nothing to update: every size is already on the current artwork.',
    live_job_in_flight:
        'Not updated: a size on the earlier art still has a digitizer job in progress. Update to current once the digitizer has delivered or the job is cancelled.',
    colorway_bound_to_older_version:
        "Not updated: a product uses a thread colorway made for the earlier art. Change that product's colorway first, then update to current.",
    live_store_unapproved:
        "The update is held: a live store's client has not approved the current art. It waits until those clients approve the current art or the art is replaced.",
    size_aspect_mismatch:
        "Not updated: a size's shape does not match the current art, so moving it would stretch it.",
    no_current_version:
        'This artwork has no current version to update to.',
}

const SEND_FALLBACK =
    'Could not send this artwork for digitization. Please try again.'
const UPDATE_FALLBACK =
    'Could not update this artwork to the current version. Nothing was changed.'

export function refusalMessage(err, fallback = SEND_FALLBACK) {
    const data = err?.response?.data || {}
    const known = data.reason && REFUSAL_MESSAGES[data.reason]
    if (known) return known
    if (typeof data.error === 'string' && data.error.trim()) {
        return data.error.trim()
    }
    return fallback
}

function namedList(items) {
    return (Array.isArray(items) ? items : []).filter(
        (x) => x && x.id != null,
    )
}

/** The stores a refused update names (409 live_store_unapproved). */
export function refusalStores(err) {
    return namedList(err?.response?.data?.stores).map((s) => ({
        id: s.id,
        name: s.name || `Store ${s.id}`,
    }))
}

/** The jobs a refused update names (409 live_job_in_flight). */
export function refusalJobs(err) {
    return namedList(err?.response?.data?.jobs)
}

/** "Moved N sizes to the current artwork." from the update's 200 body. */
export function movedMessage(body) {
    const n = Array.isArray(body?.decoration_ids)
        ? body.decoration_ids.length
        : 0
    return `Moved ${n} size${n === 1 ? '' : 's'} to the current artwork.`
}

export function sentMessage(body) {
    const n = Array.isArray(body?.jobs) ? body.jobs.length : 0
    return `Sent ${n} size${n === 1 ? '' : 's'} for digitization.`
}

export default {
    name: 'DecorationDigitizationCard',
    components: { DigitizeAtSizeDialog, ReworkPanel },
    props: {
        artworkId: {
            type: [Number, String],
            required: true,
        },
        /** The page's flattened decoration cards (allDecorationCards). */
        cards: {
            type: Array,
            default: () => [],
        },
        /** The caller's capabilities (the layout's injected list). */
        capabilities: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['changed'],
    data() {
        return {
            sending: false,
            sendError: null,
            updateOpen: false,
            updateReturnFocus: null,
            updating: false,
            updateError: null,
            updateWarning: [],
            reworkFor: null,
            reworkRecordId: null,
            reworkLoading: false,
            reworkPending: false,
            reworkError: null,
            reworkPrefill: { loaded: false, instruction: null, canSubmit: false },
        }
    },
    computed: {
        canSend() {
            return this.capabilities.includes(SEND_CAP)
        },
        canUpdate() {
            return this.capabilities.includes(UPDATE_CAP)
        },
        olderCount() {
            // The endpoint moves EVERY live size on an earlier version,
            // digitizable or not, so the dialog counts them all.
            return this.cards.filter((c) => c && c.on_current_version === false)
                .length
        },
        rows() {
            return this.cards.filter((c) => c && c.digitization)
        },
        sendable() {
            // What the send would enqueue: a not-sent or failed size on
            // the artwork's CURRENT version. A size on an earlier version
            // is never covered by it (see the header), so it does not
            // make the button appear.
            return this.rows.filter(
                (c) =>
                    c.on_current_version !== false &&
                    SENDABLE.has(this.stateOf(c)),
            )
        },
        sendLabel() {
            if (this.sending) return 'Sending...'
            const allFailed = this.sendable.every(
                (c) => this.stateOf(c) === 'failed',
            )
            return allFailed
                ? 'Resend for digitization'
                : 'Send for digitization'
        },
    },
    methods: {
        stateOf(card) {
            return card?.digitization?.state || 'not_sent'
        },
        stateLabel(card) {
            const state = this.stateOf(card)
            if (state === 'digitized') {
                const n = card?.digitized_version?.version_number
                return n != null ? `Digitized v${n}` : 'Digitized'
            }
            return STATE_LABELS[state] || state
        },
        dimsLabel(card) {
            const fmt = (v) => (v != null ? Number(v).toFixed(2) : '?')
            return `${fmt(card.width_in)}" x ${fmt(card.height_in)}"`
        },
        fileName(card) {
            return card?.digitized_version?.filename || ''
        },
        // #728: a returned file shows under a readable name; the generated
        // name it is stored under stays one hover away (support, back office).
        storedNameHint(card) {
            const stored = card?.digitized_version?.stored_filename
            return stored && stored !== this.fileName(card) ? `Stored as ${stored}` : undefined
        },
        stitchLabel(card) {
            let n = card?.digitized_version?.stitch_count
            if (n == null && card?.spec?.stitch_count_final) {
                n = card.spec.stitch_count
            }
            if (n == null || !Number.isFinite(Number(n))) return ''
            return `${Number(n).toLocaleString()} stitches`
        },
        hasJob(card) {
            const ids = card?.digitization?.job_ids
            return Array.isArray(ids) && ids.length > 0
        },
        reworkRecordOf(card) {
            // A client's change request first (its words prefill the box),
            // then the size's other current records.
            const first = (list) =>
                (Array.isArray(list) ? list : []).find(
                    (c) => c && c.approval_record_id != null,
                )
            const hit =
                first(card?.changes_requested_clients) ||
                first(card?.awaiting_clients) ||
                first(card?.approved_clients)
            return hit ? hit.approval_record_id : null
        },
        canRework(card) {
            return (
                this.canSend &&
                this.stateOf(card) === 'digitized' &&
                this.hasJob(card) &&
                this.reworkRecordOf(card) != null
            )
        },
        reworkUnavailable(card) {
            return (
                this.canSend &&
                this.stateOf(card) === 'digitized' &&
                this.hasJob(card) &&
                this.reworkRecordOf(card) == null
            )
        },
        notify(action, message) {
            // The shared app toast. Best effort: the inline message is the
            // record of a refusal.
            if (this.$store && typeof this.$store.dispatch === 'function') {
                this.$store.dispatch(action, message)
            }
        },
        onSent(body) {
            this.sendError = null
            this.notify('raiseSuccess', sentMessage(body))
            this.$emit('changed')
        },
        async send() {
            if (this.sending || !this.canSend) return
            this.sending = true
            this.sendError = null
            try {
                const resp = await axios.post(
                    `/api/artworks/${this.artworkId}/send_to_digitizer`,
                    {},
                    { [SUPPRESS_ERROR_TOAST]: true },
                )
                this.onSent(resp?.data)
            } catch (err) {
                this.sendError = refusalMessage(err)
                this.notify('raiseError', this.sendError)
            } finally {
                this.sending = false
            }
        },
        jobLine(job) {
            const card = this.cards.find((c) => c && c.id === job.decoration_id)
            const size = card ? this.dimsLabel(card) : `Size ${job.decoration_id}`
            return job.awaiting_harvest
                ? `${size}: the digitizer accepted it, but its file has not been received yet`
                : `${size}: being digitized`
        },
        openUpdate() {
            if (this.updating || !this.canUpdate) return
            this.updateReturnFocus = document.activeElement
            this.updateOpen = true
            // Focus lands on Cancel, the safe default, as the dialog opens.
            this.$nextTick(() => this.$refs.updateCancel?.focus())
        },
        cancelUpdate() {
            if (this.updating) return
            this.closeUpdate()
        },
        closeUpdate() {
            this.updateOpen = false
            // Back to the control that opened it.
            const back = this.updateReturnFocus
            this.updateReturnFocus = null
            if (back && typeof back.focus === 'function') {
                this.$nextTick(() => back.focus())
            }
        },
        async confirmUpdate() {
            if (this.updating || !this.canUpdate) return
            this.updating = true
            this.updateError = null
            this.updateWarning = []
            try {
                const resp = await axios.post(
                    `/api/artworks/${this.artworkId}/update_to_current`,
                    {},
                    { [SUPPRESS_ERROR_TOAST]: true },
                )
                const body = resp?.data
                const moved = movedMessage(body)
                const stores = namedList(body?.unapproved_live_stores)
                if (stores.length) {
                    this.updateWarning = stores.map((s) => ({
                        id: s.id,
                        name: s.name || `Store ${s.id}`,
                    }))
                    // One toast: a second would replace the first.
                    this.notify(
                        'raiseInfo',
                        `${moved} These live stores will use the new art, and their clients need to re-approve it: ${this.updateWarning
                            .map((s) => s.name)
                            .join(', ')}.`,
                    )
                } else {
                    this.notify('raiseSuccess', moved)
                }
                this.$emit('changed')
            } catch (err) {
                const stores = refusalStores(err)
                this.updateError = {
                    message: refusalMessage(err, UPDATE_FALLBACK),
                    stores,
                    jobs: refusalJobs(err),
                }
                const names = stores.map((s) => s.name).join(', ')
                this.notify(
                    'raiseError',
                    names
                        ? `${this.updateError.message} Stores: ${names}.`
                        : this.updateError.message,
                )
                // A card that was stale (another tab already updated it)
                // is fixed by a re-read, not by pressing again.
                if (err?.response?.data?.reason === 'nothing_to_update') {
                    this.$emit('changed')
                }
            } finally {
                this.updating = false
                this.closeUpdate()
            }
        },
        async openRework(card) {
            const recordId = this.reworkRecordOf(card)
            if (recordId == null || this.reworkLoading) return
            this.reworkFor = card.id
            this.reworkRecordId = recordId
            this.reworkError = null
            this.reworkPrefill = { loaded: false, instruction: null, canSubmit: false }
            this.reworkLoading = true
            try {
                const resp = await axios.get(
                    `/api/approval_records/${recordId}/rework`,
                )
                const data = resp?.data || {}
                this.reworkPrefill = {
                    loaded: true,
                    instruction: data.instruction || null,
                    canSubmit: data.can_submit === true,
                }
            } catch (err) {
                this.reworkError =
                    err?.response?.data?.error ||
                    'Could not open the rework for this size.'
            } finally {
                this.reworkLoading = false
            }
        },
        closeRework() {
            this.reworkFor = null
            this.reworkRecordId = null
            this.reworkError = null
            this.reworkPrefill = { loaded: false, instruction: null, canSubmit: false }
        },
        async submitRework(instruction) {
            if (this.reworkRecordId == null || this.reworkPending) return
            this.reworkPending = true
            this.reworkError = null
            try {
                await axios.post(
                    `/api/approval_records/${this.reworkRecordId}/rework`,
                    { instruction },
                )
                this.closeRework()
                this.notify('raiseSuccess', 'Sent back to the digitizer.')
                this.$emit('changed')
            } catch (err) {
                // The draft survives in ReworkPanel; say why here.
                this.reworkError =
                    err?.response?.data?.error ||
                    'Could not send the rework. Please try again.'
            } finally {
                this.reworkPending = false
            }
        },
    },
}
</script>

<style scoped>
.ddc {
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 26px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.ddc__head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 9px;
}
.ddc__title {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1, #212121);
    font-family: var(--tf-font-display, sans-serif);
    margin: 0;
}
.ddc__sub,
.ddc__muted {
    font-size: 12.5px;
    color: var(--tf-fg-4, #616161);
    margin: 0;
}
.ddc__spacer {
    flex: 1;
}
.ddc__row {
    border-top: 1px solid var(--tf-border, #e0e0e0);
    padding-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.ddc__row-main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.ddc__method {
    display: inline-flex;
    align-items: center;
    background: #ede7f6;
    color: #5e35b1;
    font-size: 11.5px;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: var(--tf-radius-pill, 9999px);
}
.ddc__dims {
    font-size: 13px;
    color: var(--tf-fg-2, #424242);
}
.ddc__state {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: var(--tf-radius-pill, 9999px);
    white-space: nowrap;
    background: #f5f5f5;
    color: var(--tf-fg-2, #424242);
}
.ddc__state--digitized {
    background: #e6f7ed;
    color: #12805a;
}
.ddc__state--digitizing {
    background: #e3f2fd;
    color: #0d5c9e;
}
.ddc__state--not_sent {
    background: #fff3e0;
    color: #a15c00;
}
.ddc__state--failed {
    background: #fdecea;
    color: #b42318;
}
.ddc__file {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: baseline;
}
.ddc__file-name {
    font-size: 13px;
    color: var(--tf-fg-2, #424242);
    word-break: break-all;
}
.ddc__notice {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    color: var(--tf-fg-2, #424242);
    background: var(--tf-surface-alt, #f9fafb);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: var(--tf-radius-md, 6px);
    padding: 6px 10px;
}
.ddc__rework {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
}
.ddc__error {
    margin: 0;
    font-size: 12.5px;
    color: var(--tf-chip-error-fg, #b42318);
}
.ddc__error-line {
    margin: 0;
}
.ddc__list {
    margin: 4px 0 0;
    padding-left: 18px;
}
.ddc__warning {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 12.5px;
    color: #8a4b00;
    background: #fff8e1;
    border: 1px solid #ffe0a3;
    border-radius: var(--tf-radius-md, 6px);
    padding: 8px 12px;
}
.ddc-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.ddc-dialog {
    background: var(--tf-bg, #fff);
    border-radius: 8px;
    width: min(480px, 92vw);
    padding: 18px 20px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.ddc-dialog__title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}
.ddc-dialog__line {
    margin: 0;
    font-size: 13.5px;
    color: var(--tf-fg-2, #424242);
}
.ddc-dialog__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
}
.ddc__empty {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
}
.ddc__btn {
    appearance: none;
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: var(--tf-radius-pill, 9999px);
    padding: 7px 14px;
    font-size: 13px;
    cursor: pointer;
    color: var(--tf-fg-2, #424242);
    display: inline-flex;
    align-items: center;
}
.ddc__btn--sm {
    padding: 5px 11px;
    font-size: 12px;
}
.ddc__btn--primary {
    background: var(--tf-primary, #119ae9);
    border-color: var(--tf-primary, #119ae9);
    color: #ffffff;
    font-weight: 500;
}
.ddc__btn:disabled {
    cursor: default;
    opacity: 0.6;
}
</style>
